import { action, makeObservable, observable, runInAction } from 'mobx';
import { Maybe } from '../../core';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';

export class RetryJobWindowState
  extends StoreNode {

  readonly nodeType = 'RetryJobWindow';

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'windowListener'>(this, {
      isLoading: observable,
      forceRedownload: observable,
      submit: action,
      windowListener: action,
      open: action,
      close: action,
    });

    this.window.listen(
      this.windowListener);
  }

  readonly window = new WindowState(this.store);

  isLoading: boolean = false;
  forceRedownload: boolean = false;

  jobId: Maybe<string>;
  teamId: Maybe<string>;
  sourceType: Maybe<string>;

  async submit() {
    this.isLoading = true;

    const input: { id: string; forceRedownload?: boolean } = { id: this.jobId || '' };
    if (this.forceRedownload) {
      input.forceRedownload = true;
    }
    const [result, err] = await this.store.apiService.retryJob({ input });

    if (err) {
      notifyError(this, 'Could not retry job processing.');
      return this.handleSubmitFinally();
    }

    notifySuccess(this, 'Job processing restarted.');

    const retryJobData = result?.retryJob?.job;
    if (retryJobData && this.jobId) {
      this.store.jobManager.patchJob(this.jobId, retryJobData);
    }

    if (this.teamId) {
      await this.store.teamDashboardPage.refreshJobs();
    } else {
      await this.store.trainerDashboardPage.refreshJobs();
    }
    runInAction(() => {
      this.handleSubmitFinally();
      this.emit('JobRetried');
    });
  }

  handleSubmitFinally() {
    runInAction(() => {
      this.isLoading = false;
      this.close();
    });
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  };

  open({ jobId, teamId, sourceType }: { jobId: string; teamId?: string; sourceType?: string }) {
    this.dispatch('Overlays', 'openWindow', { name: 'RetryJobWindow' });

    this.jobId = jobId;
    this.teamId = teamId;
    this.sourceType = sourceType;
  }

  close() {
    this.jobId = null;
    this.teamId = null;
    this.sourceType = null;
    this.forceRedownload = false;

    this.emit('close');
    this.dispatch('Overlays', 'closeWindow');
  }
}
